import React, { useState } from "react";
import { PlateResult } from "./PlateGameRun.tsx";
import { NAME_MAX } from "./plateLeaderboard.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";

interface PlateGameSummaryProps {
    result: PlateResult;
    /** The round made the top ten and has not been saved or skipped yet. */
    canSave: boolean;
    /** 0-based rank once saved; null before. */
    savedRank: number | null;
    initialName: string;
    onSave(name: string): void;
    onSkip(): void;
    onPlayAgain(): void;
    onChangeMode(): void;
}

/**
 * The bell: the score, a chance to put it on the leaderboard, and the facts
 * worth another look. Saving is always a deliberate press (ADR 0060 §5), so a
 * sibling's score is never filed under the last name used.
 */
export function PlateGameSummary({
    result,
    canSave,
    savedRank,
    initialName,
    onSave,
    onSkip,
    onPlayAgain,
    onChangeMode,
}: PlateGameSummaryProps) {
    const t = useTranslation();
    const [name, setName] = useState(initialName);

    return (
        <div className="quizSummary plateSummary">
            <p className="quizSummaryCounts">{t("flashCards.platesTimeUp")}</p>
            <p className="quizSummaryScore">{result.score}</p>
            <p className="quizSummaryScoreLabel">{t("flashCards.platesPointsCaption")}</p>
            <p className="quizSummaryCounts">
                {format(t("flashCards.platesCountsTemplate"), { hits: result.hits, wrong: result.wrongPlates })}
            </p>

            {canSave && (
                <form
                    className="plateSave"
                    onSubmit={(e) => {
                        e.preventDefault();
                        if (name.trim()) {
                            onSave(name);
                        }
                    }}
                >
                    <p className="plateSaveTitle">{t("flashCards.platesMadeTheBoard")}</p>
                    <label className="plateSaveLabel" htmlFor="plateSaveName">
                        {t("flashCards.platesNameLabel")}
                    </label>
                    <div className="plateSaveRow">
                        <input
                            id="plateSaveName"
                            className="plateSaveInput"
                            type="text"
                            autoComplete="off"
                            autoFocus
                            maxLength={NAME_MAX}
                            placeholder={t("flashCards.platesNamePlaceholder")}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                        <button type="submit" className="plateSaveButton" disabled={!name.trim()}>
                            {t("flashCards.platesSave")}
                        </button>
                    </div>
                    <button type="button" className="quizChangeMode" onClick={onSkip}>
                        {t("flashCards.platesSkip")}
                    </button>
                </form>
            )}

            {savedRank !== null && (
                <p className="quizSummaryBest quizSummaryBest--new">
                    {format(t("flashCards.platesRankTemplate"), { n: savedRank + 1 })}
                </p>
            )}

            {result.misses.length > 0 && (
                <div className="quizSummaryMisses">
                    <h3>{t("flashCards.worthAnotherLookHeading")}</h3>
                    <ul>
                        {result.misses.map((miss, i) => (
                            <li key={i}>
                                <span className="quizSummaryMissProblem">
                                    {miss.front} = {miss.answer}
                                </span>
                                <span className="quizSummaryMissYours">
                                    {format(t("flashCards.platesYouHitTemplate"), { n: miss.picked })}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <button type="button" className="quizPlayAgain" onClick={onPlayAgain}>
                {t("flashCards.playAgain")}
            </button>
            <button type="button" className="quizChangeMode" onClick={onChangeMode}>
                {t("flashCards.platesChangeMode")}
            </button>
        </div>
    );
}
